import { Alert, Autocomplete, Box, FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { AiAssistantRoleTool, AiAssistantRoleToolStatus } from '../../core/types/data-models';

/**
 * Informational suggestions only — the backend is the source of truth
 * (validated against services/agents/tools/*.tools.ts at write time).
 */
export const KNOWN_AGENT_TOOLS: { name: string; bundle: string }[] = [
  { name: 'get_project_documents', bundle: 'project-documents' },
  { name: 'get_document_sections', bundle: 'project-documents' },
  { name: 'get_section_content', bundle: 'project-documents' },
  { name: 'get_document_content', bundle: 'project-documents' },
  { name: 'get_document_summary', bundle: 'project-documents' },
  { name: 'get_project_document_types', bundle: 'project-documents' },
  { name: 'search_entities', bundle: 'project-knowledge-graph' },
  { name: 'get_entity_details', bundle: 'project-knowledge-graph' },
  { name: 'get_entity_relations', bundle: 'project-knowledge-graph' },
  { name: 'get_section_entities', bundle: 'project-knowledge-graph' },
  { name: 'get_entity_related_sections', bundle: 'project-knowledge-graph' },
  { name: 'get_project_tasks', bundle: 'project-tasks' },
  { name: 'create_task', bundle: 'project-actions' },
  { name: 'start_document_generation', bundle: 'project-actions' },
  { name: 'start_document_metadata', bundle: 'project-actions' },
  { name: 'start_document_visualization', bundle: 'project-actions' },
  { name: 'recommend_action', bundle: 'project-actions' },
  { name: 'edit_document_section_content', bundle: 'project-document-edits' },
  { name: 'split_document_section', bundle: 'project-document-edits' },
  { name: 'merge_document_sections', bundle: 'project-document-edits' },
  { name: 'add_document_section', bundle: 'project-document-edits' },
  { name: 'update_entity_details', bundle: 'project-knowledge-graph-edits' },
  { name: 'add_entity_relation', bundle: 'project-knowledge-graph-edits' },
  { name: 'update_entity_relation', bundle: 'project-knowledge-graph-edits' },
];

export interface AiAssistantRoleToolDialogProps {
  open: boolean;
  initial?: AiAssistantRoleTool | null;
  /**
   * Role this tool attaches to when not marked default. Omit when managing
   * default (all-roles) tools directly — the default toggle is hidden and
   * the tool is always saved with a null `assistantRoleKey`.
   */
  assistantRoleKey?: string;
  onClose: () => void;
}

export function AiAssistantRoleToolDialog({ open, initial, assistantRoleKey, onClose }: AiAssistantRoleToolDialogProps) {
  const qc = useQueryClient();
  const [toolName, setToolName] = useState('');
  const [order, setOrder] = useState('0');
  const [status, setStatus] = useState<AiAssistantRoleToolStatus>('ACTIVE');
  const [isDefault, setIsDefault] = useState(!assistantRoleKey);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setToolName(initial?.toolName ?? '');
      setOrder(initial?.order != null ? String(initial.order) : '0');
      setStatus(initial?.status ?? 'ACTIVE');
      setIsDefault(initial ? initial.assistantRoleKey === null : !assistantRoleKey);
      setError(null);
    }
  }, [open, initial, assistantRoleKey]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        assistantRoleKey: isDefault ? null : assistantRoleKey,
        toolName: toolName.trim(),
        order: order !== '' ? Number(order) : null,
        status,
      };
      if (initial) return resources.aiAssistantRoleTools.update(initial.id, payload);
      return resources.aiAssistantRoleTools.create(payload);
    },
    onSuccess: () => {
      // Prefix match — invalidates both a role-page's `['aiAssistantRoleTools', key]`
      // list and the Default Tools page's unscoped list.
      qc.invalidateQueries({ queryKey: ['aiAssistantRoleTools'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit Tool' : 'New Tool'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="sm"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Autocomplete
            freeSolo
            options={KNOWN_AGENT_TOOLS.map((t) => t.name)}
            groupBy={(name) => KNOWN_AGENT_TOOLS.find((t) => t.name === name)?.bundle ?? ''}
            value={toolName}
            onInputChange={(_e, val) => setToolName(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tool name"
                required
                autoFocus
                helperText="Must match an AgentToolDefinition.name from services/agents/tools/*.tools.ts"
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Order"
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              helperText="Display order within the role (lower = first)"
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as AiAssistantRoleToolStatus)}
              sx={{ flex: 1 }}
            >
              {(['ACTIVE', 'DISABLED'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
          {assistantRoleKey && (
            <FormControlLabel
              control={<Switch checked={isDefault} onChange={(_e, checked) => setIsDefault(checked)} />}
              label="Default tool — available to all AI Assistant Roles"
            />
          )}
        </Stack>
      </Box>
    </FormDialog>
  );
}
